/* ============================================================
   Epilekton — Polish & Motion runtime
   Session 09. Vanilla JS, deferred, no framework.
   Pairs with assets/scroll-reveal.css.
   ============================================================ */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Under reduced motion, skip IntersectionObserver entirely — just reveal everything
  // immediately and bail before any animation work happens.
  if (reduceMotion) {
    function revealAllNow() {
      document.querySelectorAll('[data-reveal]').forEach(function (el) {
        el.setAttribute('data-reveal-state', 'visible');
      });
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', revealAllNow);
    } else {
      revealAllNow();
    }
    return;
  }

  // ----------------------------------------------------------
  // 1. Scroll-reveal (IntersectionObserver)
  // ----------------------------------------------------------
  // Auto-apply data-reveal to known section blocks so we don't
  // have to edit every section/template by hand.
  var REVEAL_SELECTORS = [
    '.epi-hero__content',
    '.epi-hero__ctas',
    '.epi-value-props__item',
    '.epi-category-grid__tile',
    '.epi-featured-products__heading',
    '.epi-featured-products__grid',
    '.epi-editorial-split__media',
    '.epi-editorial-split__copy',
    '.epi-testimonials__quote',
    '.epi-newsletter__heading'
  ];

  function markRevealTargets() {
    var stagger = 80;
    document.querySelectorAll(REVEAL_SELECTORS.join(',')).forEach(function (el, idx) {
      if (el.hasAttribute('data-reveal')) return;
      // Skip product cards inside collection grids (popcorn effect)
      if (el.closest('.epi-product-grid')) return;
      // Skip on PDP
      if (document.body.classList.contains('template-product')) return;

      el.setAttribute('data-reveal', '');
      el.setAttribute('data-reveal-state', 'hidden');

      // Stagger siblings inside the same parent up to ~600ms
      var siblingIdx = Array.prototype.indexOf.call(el.parentNode.children, el);
      var delay = Math.min(siblingIdx * stagger, 600);
      el.style.setProperty('--reveal-delay', delay + 'ms');
    });
  }

  function startReveal() {
    if (reduceMotion || !('IntersectionObserver' in window)) {
      document.querySelectorAll('[data-reveal]').forEach(function (el) {
        el.setAttribute('data-reveal-state', 'visible');
      });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.setAttribute('data-reveal-state', 'visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });
    document.querySelectorAll('[data-reveal][data-reveal-state="hidden"]').forEach(function (el) {
      io.observe(el);
    });
  }

  // ----------------------------------------------------------
  // 2. Back-to-top FAB
  // ----------------------------------------------------------
  function mountBackToTop() {
    if (document.querySelector('.epi-back-to-top')) return;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'epi-back-to-top';
    btn.setAttribute('aria-label', (window.epiStrings && window.epiStrings.backToTop) || 'Back to top');
    btn.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 14l6-6 6 6"/></svg>';
    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    });
    document.body.appendChild(btn);

    var ticking = false;
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        btn.classList.toggle('is-visible', window.scrollY > 1200);
        ticking = false;
      });
    }, { passive: true });
  }

  // ----------------------------------------------------------
  // 3. Cart icon pulse (MutationObserver on cart-icon-bubble)
  // ----------------------------------------------------------
  function watchCartIcon() {
    var icon = document.getElementById('cart-icon-bubble');
    if (!icon) return;
    var lastCount = readCount(icon);

    var mo = new MutationObserver(function () {
      var newCount = readCount(icon);
      if (newCount !== lastCount) {
        lastCount = newCount;
        pulse(icon);
      }
    });
    mo.observe(icon, { childList: true, subtree: true, characterData: true });
  }

  function readCount(icon) {
    var bubble = icon.querySelector('.cart-count-bubble span[aria-hidden="true"]');
    return bubble ? bubble.textContent.trim() : '';
  }

  function pulse(icon) {
    icon.classList.remove('is-pulsing');
    // Force reflow to restart animation
    void icon.offsetWidth;
    icon.classList.add('is-pulsing');
    setTimeout(function () { icon.classList.remove('is-pulsing'); }, 400);
  }

  // ----------------------------------------------------------
  // 4. Quick-add success state + fly-to-cart
  // ----------------------------------------------------------
  function bindAddToCart() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-quick-add], [data-pdp-atc], .product-form__submit');
      if (!btn) return;
      if (btn.disabled) return;

      // Find source image
      var sourceImg = null;
      var card = btn.closest('.epi-card-product');
      if (card) sourceImg = card.querySelector('.epi-card-product__image');
      if (!sourceImg) {
        var pdp = btn.closest('main, body');
        sourceImg = pdp && pdp.querySelector('.product__media img, .product-media img, .epi-pdp__gallery img');
      }

      // Fly-to-cart animation (skip under reduced motion)
      if (sourceImg && !reduceMotion) flyToCart(sourceImg);

      // Add success state to quick-add button
      if (btn.matches('[data-quick-add]')) {
        if (!btn.dataset.successLabel) {
          btn.dataset.successLabel = (window.epiStrings && window.epiStrings.addedToCart) || 'Προστέθηκε';
        }
        var attrObs = new MutationObserver(function () {
          if (!btn.hasAttribute('aria-busy') && !btn.disabled) {
            attrObs.disconnect();
            btn.classList.add('is-success');
            setTimeout(function () { btn.classList.remove('is-success'); }, 1200);
            document.dispatchEvent(new CustomEvent('cart:added'));
          }
        });
        attrObs.observe(btn, { attributes: true, attributeFilter: ['aria-busy', 'disabled'] });
        // Safety timeout
        setTimeout(function () { attrObs.disconnect(); }, 6000);
      }
    }, true);
  }

  function flyToCart(sourceImg) {
    var target = document.getElementById('cart-icon-bubble');
    if (!target) return;
    var srcRect = sourceImg.getBoundingClientRect();
    var tgtRect = target.getBoundingClientRect();
    if (!srcRect.width || !tgtRect.width) return;

    var clone = sourceImg.cloneNode(true);
    clone.className = 'epi-fly-clone';
    clone.removeAttribute('loading');
    clone.removeAttribute('srcset');
    clone.removeAttribute('sizes');
    clone.style.left = srcRect.left + 'px';
    clone.style.top = srcRect.top + 'px';
    clone.style.width = srcRect.width + 'px';
    clone.style.height = srcRect.height + 'px';
    clone.style.opacity = '1';
    document.body.appendChild(clone);

    // Force layout, then animate
    void clone.offsetWidth;
    var dx = (tgtRect.left + tgtRect.width / 2) - (srcRect.left + srcRect.width / 2);
    var dy = (tgtRect.top + tgtRect.height / 2) - (srcRect.top + srcRect.height / 2);
    clone.style.transform = 'translate(' + dx + 'px, ' + dy + 'px) scale(0.3)';
    clone.style.opacity = '0';

    setTimeout(function () { clone.remove(); }, 600);
  }

  // ----------------------------------------------------------
  // 5. Bootstrap
  // ----------------------------------------------------------
  function init() {
    markRevealTargets();
    startReveal();
    mountBackToTop();
    watchCartIcon();
    bindAddToCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
